import { useEffect, useState } from "react";
import { toast } from "sonner";
import { gestApi } from "./GestAuthContext";

const roleOptions = [
  ["admin", "Amministratore"],
  ["ufficio", "Ufficio"],
  ["produzione", "Produzione"],
  ["posa", "Posa"],
];
const roleLabel = Object.fromEntries(roleOptions);
const roleColour = { admin: "#0a1420", ufficio: "#3b82f6", produzione: "#f97316", posa: "#16a34a" };

function UserForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "ufficio" });
  const [busy, setBusy] = useState(false);
  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await gestApi.post("/users", form);
      toast.success("Utente creato");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-[500px] bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="gest-user-form">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="text-[15px] font-semibold text-[#0a1420]">Nuovo utente</div>
          <button type="button" onClick={onClose} className="text-[#6e6e73] hover:text-[#0a1420] text-[20px] leading-none">×</button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Nome *</label>
            <input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="gest-user-name" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px]" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Email *</label>
            <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="gest-user-email" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px]" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Password provvisoria * (min 6 caratteri)</label>
            <input required minLength={6} type="text" value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="gest-user-password" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] font-mono" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Ruolo *</label>
            <select value={form.role} onChange={(e) => update("role", e.target.value)} data-testid="gest-user-role" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] bg-white">
              {roleOptions.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div className="text-[11px] text-[#6e6e73] mt-1">
              {form.role === "produzione" && "→ Vede solo il modulo Produzione. Nessun dato economico."}
              {form.role === "posa" && "→ Vede solo il modulo Posa. Nessun dato economico."}
              {form.role === "ufficio" && "→ Accesso completo compresi costi, ricavi e fatture."}
              {form.role === "admin" && "→ Accesso totale + gestione utenti."}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-black/5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] text-[#6e6e73]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="gest-user-save" className="px-5 py-2 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold disabled:opacity-60">
            {busy ? "Creazione…" : "Crea utente"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function GestUtenti() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await gestApi.get("/users");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.name} (${u.email})?`)) return;
    try {
      await gestApi.delete(`/users/${u.id}`);
      toast.success("Utente eliminato");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="p-5 md:p-8 max-w-[1000px] mx-auto" data-testid="gest-utenti-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-[12px] uppercase tracking-widest text-[#6e6e73]">Team</div>
          <h1 className="text-[26px] md:text-[34px] font-semibold tracking-[-0.02em] text-[#0a1420]">Utenti</h1>
          <div className="text-[13px] text-[#6e6e73] mt-1">Gestisci gli accessi alla control tower.</div>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="gest-user-new" className="px-4 py-2.5 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1a2432]">+ Nuovo utente</button>
      </div>

      {loading ? (
        <div className="text-[#6e6e73]">Caricamento…</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/[0.04] overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.2fr_1.4fr_1fr_100px] gap-3 px-5 py-3 text-[11px] uppercase tracking-widest text-[#6e6e73] border-b border-black/5">
            <div>Nome</div>
            <div>Email</div>
            <div>Ruolo</div>
            <div></div>
          </div>
          <div className="divide-y divide-black/5">
            {rows.map((u) => (
              <div key={u.id} className="grid grid-cols-[1fr_100px] md:grid-cols-[1.2fr_1.4fr_1fr_100px] gap-3 px-5 py-3.5 items-center" data-testid={`gest-user-row-${u.id}`}>
                <div className="min-w-0">
                  <div className="text-[14px] font-medium text-[#0a1420] truncate">{u.name}</div>
                  <div className="text-[12px] text-[#6e6e73] md:hidden truncate">{u.email}</div>
                </div>
                <div className="hidden md:block text-[13px] text-[#6e6e73] truncate">{u.email}</div>
                <div className="hidden md:block">
                  <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium text-white" style={{ backgroundColor: roleColour[u.role] || "#6e6e73" }}>{roleLabel[u.role] || u.role}</span>
                </div>
                <div className="text-right">
                  <button onClick={() => del(u)} data-testid={`gest-user-del-${u.id}`} className="text-[11px] text-red-500 hover:text-red-700">Elimina</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && <UserForm onClose={() => setShowForm(false)} onSaved={load} />}
    </div>
  );
}
